import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import cache, { CacheTTL } from '../utils/cache';
import Navbar from '../student/Navbar';
import AssistantSidebar from './AssistantSidebar';
import Loader from '../components/Loader';
import withRoleGuard from '../hoc/withRoleGuard';
import {
  FaUserAlt,
  FaCalendarAlt,
  FaUsers,
  FaPlus,
  FaSearch,
  FaChartBar,
  FaCalendarCheck,
  FaFolderOpen,
} from 'react-icons/fa';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { isToday } from '../utils/dateUtils';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const AssistantDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [patientStats, setPatientStats] = useState({ totalPatients: 0, monthlyTrends: [] });
  const [loading, setLoading] = useState(true);
  const [, setError] = useState('');
  const navigate = useNavigate();
  const { user, token } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      if (!user || !token) {
        setError('Please log in to view your dashboard.');
        setLoading(false);
        return;
      }

      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };

        // Use lightweight stats endpoint instead of fetching all 6000+ patients
        const [appointmentsRes, statsData] = await Promise.all([
          cache.getOrSet(
            'dashboard_appointments',
            async () => {
              const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/appointments`, config);
              return res.data || [];
            },
            CacheTTL.SHORT
          ),
          cache.getOrSet(
            'dashboard_patient_stats',
            async () => {
              const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/patients/stats`, config);
              return res.data;
            },
            CacheTTL.MEDIUM
          )
        ]);

        setAppointments(appointmentsRes);
        setPatientStats(statsData);

      } catch (err) {
        setAppointments([]);
        setPatientStats({ totalPatients: 0, monthlyTrends: [] });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, token, navigate]);

  const todayAppointments = appointments.filter(a => isToday(a.date));
  const totalPatients = patientStats.totalPatients;

  const months = patientStats.monthlyTrends?.map(t => t.month) || [];
  const patientCounts = patientStats.monthlyTrends?.map(t => t.count) || [];

  // Chart data for patient trends
  const patientTrendsData = {
    labels: months,
    datasets: [{
      label: 'New Patients',
      data: patientCounts,
      backgroundColor: 'rgba(0, 119, 182, 0.1)',
      borderColor: '#0077B6',
      borderWidth: 3,
      tension: 0.4,
      fill: true,
      pointBackgroundColor: '#0077B6',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 6,
      pointHoverRadius: 8
    }]
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#0077B6',
        bodyColor: '#333333',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        bodyFont: { family: "'Inter', sans-serif" },
        titleFont: { family: "'Inter', sans-serif", weight: 'bold' }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: { family: "'Inter', sans-serif", size: 12 }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          borderDash: [2, 4],
          color: 'rgba(0, 0, 0, 0.06)'
        },
        ticks: {
          precision: 0,
          font: { family: "'Inter', sans-serif", size: 12 }
        }
      }
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="flex h-screen bg-gray-50">
      <AssistantSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6 bg-gradient-to-br from-blue-50 to-white">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6 sm:mb-8">
              <h1 className="text-2xl sm:text-3xl font-bold text-[#0077B6] mb-2">
                Welcome back, {user?.name || 'Assistant'}!
              </h1>
              <p className="text-sm sm:text-base text-gray-600">Here's what's happening today</p>
            </div>

            {/* Quick Stats - Simple and Clean */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
              <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-center">
                  <div className="bg-blue-100 p-2 sm:p-3 rounded-lg mr-3 sm:mr-4">
                    <FaCalendarAlt className="text-[#0077B6] text-lg sm:text-xl" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Today's Appointments</p>
                    <p className="text-xl sm:text-2xl font-bold text-[#0077B6]">{todayAppointments.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-center">
                  <div className="bg-green-100 p-2 sm:p-3 rounded-lg mr-3 sm:mr-4">
                    <FaUsers className="text-green-600 text-lg sm:text-xl" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Total Patients</p>
                    <p className="text-xl sm:text-2xl font-bold text-green-600">{totalPatients}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions and Patient Trends */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
              {/* Quick Actions */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="p-4 sm:p-6 border-b border-gray-100">
                  <h2 className="text-lg sm:text-xl font-bold text-[#0077B6]">Quick Actions</h2>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">Common tasks you need to do</p>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <Link
                      to="/assistant/appointments"
                      className="flex flex-col items-center p-3 sm:p-4 rounded-lg hover:bg-blue-50 transition-colors group"
                    >
                      <div className="bg-blue-500 p-2 sm:p-3 rounded-lg mb-2 sm:mb-3 group-hover:bg-blue-600 transition-colors">
                        <FaPlus className="text-white text-lg sm:text-xl" />
                      </div>
                      <span className="text-xs sm:text-sm font-medium text-gray-700 text-center">New Appointment</span>
                    </Link>

                    <Link
                      to="/assistant/patients"
                      className="flex flex-col items-center p-3 sm:p-4 rounded-lg hover:bg-green-50 transition-colors group"
                    >
                      <div className="bg-green-500 p-2 sm:p-3 rounded-lg mb-2 sm:mb-3 group-hover:bg-green-600 transition-colors">
                        <FaUserAlt className="text-white text-lg sm:text-xl" />
                      </div>
                      <span className="text-xs sm:text-sm font-medium text-gray-700 text-center">Add Patient</span>
                    </Link>

                    <Link
                      to="/assistant/appointments"
                      className="flex flex-col items-center p-3 sm:p-4 rounded-lg hover:bg-purple-50 transition-colors group"
                    >
                      <div className="bg-purple-500 p-2 sm:p-3 rounded-lg mb-2 sm:mb-3 group-hover:bg-purple-600 transition-colors">
                        <FaCalendarCheck className="text-white text-lg sm:text-xl" />
                      </div>
                      <span className="text-xs sm:text-sm font-medium text-gray-700 text-center">View Schedule</span>
                    </Link>

                    <Link
                      to="/assistant/patients"
                      className="flex flex-col items-center p-3 sm:p-4 rounded-lg hover:bg-orange-50 transition-colors group"
                    >
                      <div className="bg-orange-500 p-2 sm:p-3 rounded-lg mb-2 sm:mb-3 group-hover:bg-orange-600 transition-colors">
                        <FaFolderOpen className="text-white text-lg sm:text-xl" />
                      </div>
                      <span className="text-xs sm:text-sm font-medium text-gray-700 text-center">Patient Records</span>
                    </Link>
                  </div>
                </div>
              </div>

              {/* Patient Trends Chart */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="p-4 sm:p-6 border-b border-gray-100">
                  <h2 className="text-lg sm:text-xl font-bold text-[#0077B6]">Patient Growth Trends</h2>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">New patients per month</p>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="h-48 sm:h-64">
                    <Line data={patientTrendsData} options={chartOptions} />
                  </div>
                </div>
              </div>
            </div>

            {/* Today's Appointments - Simple List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6 sm:mb-8">
              <div className="p-4 sm:p-6 border-b border-gray-100">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <h2 className="text-lg sm:text-xl font-bold text-[#0077B6]">Today's Appointments</h2>
                  <Link
                    to="/assistant/appointments"
                    className="text-[#0077B6] hover:text-blue-700 text-sm font-medium self-start sm:self-auto"
                  >
                    View All →
                  </Link>
                </div>
              </div>
              <div className="p-4 sm:p-6">
                {todayAppointments.length > 0 ? (
                  <div className="space-y-3">
                    {todayAppointments.map((appt, index) => (
                      <div key={appt._id || index} className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                          <FaCalendarAlt className="text-[#0077B6] text-sm sm:text-base" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm sm:text-base truncate">
                            {appt.patient?.fullName || appt.fullName || 'Unknown Patient'}
                          </p>
                          <p className="text-xs sm:text-sm text-gray-500 truncate">
                            {appt.time || 'No time set'} • {appt.treatment || appt.type || 'General Checkup'}
                          </p>
                        </div>
                        <span className={`px-2 sm:px-3 py-1 text-xs font-semibold rounded-full flex-shrink-0 ${
                          appt.status === 'completed' ? 'bg-green-100 text-green-800' :
                          appt.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {appt.status}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 sm:py-8">
                    <FaCalendarAlt className="text-gray-300 text-3xl sm:text-4xl mx-auto mb-3" />
                    <p className="text-gray-500 text-sm sm:text-base">No appointments scheduled for today</p>
                    <Link
                      to="/assistant/appointments"
                      className="inline-block mt-3 text-[#0077B6] hover:text-blue-700 font-medium text-sm sm:text-base"
                    >
                      Schedule an appointment
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Search */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-bold text-[#0077B6] mb-4">Quick Search</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <Link
                  to="/assistant/patients"
                  className="flex items-center p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <FaSearch className="text-gray-500 mr-3 text-sm sm:text-base" />
                  <span className="text-gray-700 text-sm sm:text-base">Find Patient</span>
                </Link>
                <Link
                  to="/assistant/appointments"
                  className="flex items-center p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <FaCalendarAlt className="text-gray-500 mr-3 text-sm sm:text-base" />
                  <span className="text-gray-700 text-sm sm:text-base">Check Schedule</span>
                </Link>
                <Link
                  to="/assistant/analytics"
                  className="flex items-center p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <FaChartBar className="text-gray-500 mr-3 text-sm sm:text-base" />
                  <span className="text-gray-700 text-sm sm:text-base">View Reports</span>
                </Link>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default withRoleGuard(['assistant'])(AssistantDashboard);
